/**
 * ThreePLBilling.jsx — orchestrator. The four resource tabs each live in
 * their own file under `/components/3pl/` so the editor surface stays small
 * and each tab can be modified without re-reading 600+ LOC of context.
 */
import React, { useEffect, useState } from 'react';
import Layout from '../components/Layout';
import { toast } from 'sonner';
import {
  Buildings, Receipt, Coins, FileText, CurrencyDollar, Package, ChartLineUp,
} from '@phosphor-icons/react';
import {
  get3plSummary, ensure3plDefaultMerchant, list3plMerchants,
} from '../lib/api';
import MerchantsTab from '../components/3pl/MerchantsTab';
import RateCardTab from '../components/3pl/RateCardTab';
import ChargesTab from '../components/3pl/ChargesTab';
import InvoicesTab from '../components/3pl/InvoicesTab';
import PnlTab from '../components/3pl/PnlTab';
import { fmt$ } from '../components/3pl/utils';

const TABS = [
  { key: 'merchants', label: 'Merchants', icon: Buildings },
  { key: 'rate-card', label: 'Rate Card', icon: CurrencyDollar },
  { key: 'charges', label: 'Charges', icon: Receipt },
  { key: 'invoices', label: 'Invoices', icon: FileText },
  { key: 'pnl', label: 'P&L', icon: ChartLineUp },
];

function KpiTile({ icon: Icon, label, value, sub, color = 'sky', testId }) {
  const colorMap = {
    sky: 'text-sky-700 bg-sky-50 border-sky-200',
    emerald: 'text-emerald-700 bg-emerald-50 border-emerald-200',
    amber: 'text-amber-700 bg-amber-50 border-amber-200',
    rose: 'text-rose-700 bg-rose-50 border-rose-200',
  };
  return (
    <div data-testid={testId} className={`border ${colorMap[color]} p-4 flex flex-col gap-1`}>
      <div className="flex items-center gap-2 text-xs uppercase tracking-wider font-semibold opacity-80">
        <Icon size={14} weight="bold" />
        {label}
      </div>
      <div className="text-2xl font-bold font-mono">{value}</div>
      {sub && <div className="text-[11px] text-muted-foreground">{sub}</div>}
    </div>
  );
}

export default function ThreePLBilling() {
  const [tab, setTab] = useState('merchants');
  const [merchants, setMerchants] = useState([]);
  const [selected, setSelected] = useState(null);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);

  const refreshAll = async () => {
    try {
      const [mRes, sRes] = await Promise.all([list3plMerchants(), get3plSummary()]);
      const list = mRes.data || [];
      setMerchants(list);
      setSummary(sRes.data);
      setSelected((cur) => {
        if (cur) {
          const updated = list.find((m) => m.id === cur.id);
          if (updated) return updated;
        }
        return list[0] || null;
      });
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not load 3PL data');
    }
  };

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        // Lazy-create the per-tenant default merchant so the editor always
        // has at least one row to work with.
        await ensure3plDefaultMerchant().catch(() => null);
        await refreshAll();
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  return (
    <Layout>
      <div className="space-y-4 p-4 md:p-6 max-w-7xl mx-auto" data-testid="three-pl-billing-page">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold tracking-tight">3PL Billing</h1>
            <p className="text-sm text-muted-foreground">
              Bill your merchant sub-clients. Storage accrues nightly; inbound/outbound on each event.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="three-pl-kpis">
          <KpiTile icon={Buildings} label="Active merchants" value={summary?.active_merchants ?? '—'} color="sky" testId="kpi-active-merchants" />
          <KpiTile icon={Receipt} label="Open charges" value={summary?.open_charges ?? '—'} sub={fmt$(summary?.open_charges_amount ?? 0) + ' to invoice'} color="amber" testId="kpi-open-charges" />
          <KpiTile icon={Coins} label="Unpaid invoices" value={summary?.unpaid_invoices ?? '—'} sub={fmt$(summary?.unpaid_invoices_amount ?? 0)} color="rose" testId="kpi-unpaid-invoices" />
          <KpiTile icon={Package} label="Selected merchant" value={selected?.name || '—'} sub={selected?.is_default ? 'Default · all traffic' : (selected ? selected.status : 'none')} color="emerald" testId="kpi-selected-merchant" />
        </div>

        <div className="flex border-b border-border overflow-x-auto" data-testid="three-pl-tabs">
          {TABS.map((t) => (
            <button
              key={t.key}
              type="button"
              onClick={() => setTab(t.key)}
              data-testid={`tab-${t.key}`}
              className={`flex items-center gap-1.5 px-4 py-2 text-sm font-semibold border-b-2 -mb-px transition ${
                tab === t.key ? 'border-foreground text-foreground' : 'border-transparent text-muted-foreground hover:text-foreground'
              }`}
            >
              <t.icon size={16} />
              {t.label}
            </button>
          ))}
        </div>

        {loading && <div className="p-6 text-center text-muted-foreground">Loading…</div>}
        {!loading && (
          <div className="pt-2">
            {tab === 'merchants' && (
              <MerchantsTab merchants={merchants} onRefresh={refreshAll} selected={selected} setSelected={setSelected} />
            )}
            {tab === 'rate-card' && (
              <RateCardTab selected={selected} />
            )}
            {tab === 'charges' && (
              <ChargesTab selected={selected} merchants={merchants} onRefresh={refreshAll} />
            )}
            {tab === 'invoices' && (
              <InvoicesTab merchants={merchants} onRefresh={refreshAll} />
            )}
            {tab === 'pnl' && (
              <PnlTab />
            )}
          </div>
        )}
      </div>
    </Layout>
  );
}
